// File: src/components/Procurement/ProcurementReportPanel.jsx
import React, { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";

import api from "@/lib/axios";
import ProcurementDecisionBreakdowns from "@/components/Procurement/ProcurementDecisionBreakdowns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

import { format, subDays, startOfMonth, endOfMonth, isAfter } from "date-fns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// ✅ Portal-based modal (fixes “modal stuck inside page” / clipping)
import { Dialog, Transition } from "@headlessui/react";

/**
 * ✅ UI statuses (human-friendly) + maps to backend statuses.
 * Backend commonly uses "Fully Received", not "Received".
 */
const UI_STATUS_OPTIONS = [
  "All",
  "Received", // maps to Fully Received
  "Partially Received",
  "Rejected",
  // Optional but useful for ops visibility:
  "Approved",
  "Dispatched",
  "Receiving",
  "Pending",
  "Pending Finance Approval",
];

function toBackendStatus(uiStatus) {
  if (!uiStatus || uiStatus === "All") return undefined;
  if (uiStatus === "Received") return "Fully Received";
  return uiStatus;
}

const EXCEPTION_OPTIONS = ["None", "Overdue (no receipt)"];

const currency = (v) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

const StatusPill = ({ value }) => {
  const map = {
    "Fully Received": "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40",
    Received: "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40",
    "Partially Received": "bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/40",
    Rejected: "bg-rose-500/15 text-rose-300 ring-1 ring-rose-500/40",
    Dispatched: "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/40",
    Approved: "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/40",
    Pending: "bg-zinc-500/15 text-zinc-300 ring-1 ring-zinc-500/40",
    "Pending Finance Approval": "bg-zinc-500/15 text-zinc-300 ring-1 ring-zinc-500/40",
    Receiving: "bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/40",
    default: "bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/40",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[value] || map.default}`}>
      {value || "—"}
    </span>
  );
};

const SkeletonRow = ({ cols = 11 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} className="p-3">
        <div className="h-3 w-full rounded bg-zinc-700/40" />
      </td>
    ))}
  </tr>
);

function detectUnitContext() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const slug = (
      sp.get("unit_slug") ||
      sp.get("slug") ||
      localStorage.getItem("activeUnitSlug") ||
      window.__UNIT_SLUG__ ||
      ""
    )
      .toString()
      .trim();

    const idRaw =
      sp.get("unit_id") ||
      sp.get("unit") ||
      localStorage.getItem("activeUnitId") ||
      window.__UNIT_ID__;

    const idNum = Number(idRaw);
    const id = Number.isFinite(idNum) && idNum > 0 ? idNum : undefined;
    return { slug: slug || undefined, id };
  } catch {
    return { slug: undefined, id: undefined };
  }
}

function clampDateRangeToToday(range) {
  const today = new Date();
  const start = range?.startDate ? new Date(range.startDate) : subDays(today, 30);
  const end = range?.endDate ? new Date(range.endDate) : today;

  const safeStart = isAfter(start, today) ? subDays(today, 30) : start;
  const safeEnd = isAfter(end, today) ? today : end;

  if (safeStart > safeEnd) {
    return { startDate: subDays(safeEnd, 30), endDate: safeEnd, key: "selection" };
  }

  return { startDate: safeStart, endDate: safeEnd, key: "selection" };
}

/** Small debounce hook (for search/vendor) */
function useDebouncedValue(value, delayMs = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// Legacy fallback (ONLY if DB po_number missing)
const legacyPoNo = (poId) => `PO-${String(Number(poId) || 0).padStart(5, "0")}`;

// Prefer real DB number when available, else report row, else legacy
function displayPoNo(poId, rowPoNo, cache) {
  const cached = poId != null ? cache?.[poId] : "";
  return cached || rowPoNo || legacyPoNo(poId);
}

/**
 * Export shape aligned:
 * - Planner ID removed
 * - "Rejected" becomes "Remaining"
 * - PO Number is the displayed one (real DB when possible)
 */
function shapeForExport(groups = [], poNumberById = {}) {
  return (groups || []).map((g) => ({
    "PO Number": displayPoNo(g.po_id, g.po_number, poNumberById),
    Vendor: g.vendor_name ?? "",
    Planner: g.planner_name ?? "",
    "Cost Centre ID": g.cost_centre_id ?? "",
    "Cost Centre (Name)": g.cost_centre_name ?? "",
    Ordered: g.ordered_total ?? "",
    Received: g.received_total ?? "",
    Remaining: g.remaining_total ?? "",
    Status: g.status ?? "",
    Expected: g.expected_delivery ? new Date(g.expected_delivery).toLocaleDateString() : "",
    Date: g.po_date ? new Date(g.po_date).toLocaleDateString() : "",
    "Net Amount (GBP)": g.net_amount ?? "",
  }));
}

export default function ProcurementReportPanel() {
  const navigate = useNavigate();

  const initial = detectUnitContext();
  const [{ slug: unitSlug, id: unitId }, setUnitCtx] = useState(initial);
  const [unitSlugInput, setUnitSlugInput] = useState(initial.slug || "");
  const [unitIdInput, setUnitIdInput] = useState(initial.id != null ? String(initial.id) : "");

  // ✅ Cache real DB po_number
  const [poNumberById, setPoNumberById] = useState({}); // { [poId]: "CHEL26367" }

  useEffect(() => {
    const onPop = () => setUnitCtx(detectUnitContext());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const getUnitParams = () => {
    const { slug, id } = detectUnitContext();
    const params = {};
    if (slug) params.unit_slug = slug;
    if (id) params.unit_id = String(id);
    return { slug, id, params };
  };

  const unitMissing = !unitSlug && !unitId;

  const saveUnitContext = () => {
    const slug = unitSlugInput.trim() || undefined;
    const idParsed = Number(unitIdInput);
    const id = Number.isFinite(idParsed) && idParsed > 0 ? idParsed : undefined;

    if (slug) localStorage.setItem("activeUnitSlug", slug);
    else localStorage.removeItem("activeUnitSlug");

    if (id) localStorage.setItem("activeUnitId", String(id));
    else localStorage.removeItem("activeUnitId");

    const sp = new URLSearchParams(window.location.search);
    if (slug) sp.set("unit_slug", slug);
    else sp.delete("unit_slug");
    if (id) sp.set("unit_id", String(id));
    else sp.delete("unit_id");

    const nextUrl = `${window.location.pathname}?${sp.toString()}${window.location.hash || ""}`;
    window.history.replaceState({}, "", nextUrl);

    setUnitCtx({ slug, id });
  };

  const [statusFilter, setStatusFilter] = useState("All");
  const [exceptionFilter, setExceptionFilter] = useState("None");
  const [vendorFilter, setVendorFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearch = useDebouncedValue(searchTerm, 400);
  const debouncedVendor = useDebouncedValue(vendorFilter, 400);

  const [dateRange, setDateRange] = useState([
    { startDate: subDays(new Date(), 30), endDate: new Date(), key: "selection" },
  ]);

  const [openCal, setOpenCal] = useState(false);
  const calRef = useRef(null);

  const VIEW_KEY = "procure:report:view-v2";

  const reportRequestId = useRef(0);

  // ✅ if backend search is still legacy, allow a one-time refetch without search and filter client-side


  useEffect(() => {
    try {
      const raw = localStorage.getItem(VIEW_KEY);
      if (!raw) return;
      const v = JSON.parse(raw);

      setStatusFilter(v.status ?? "All");
      setExceptionFilter(v.exception ?? "None");
      setVendorFilter(v.vendor ?? "");
      setSearchTerm(v.search ?? "");

      const clamped = clampDateRangeToToday({
        startDate: v.startDate ? new Date(v.startDate) : null,
        endDate: v.endDate ? new Date(v.endDate) : null,
      });

      setDateRange([clamped]);
    } catch {
      // ignore
    }
  }, []);

  const saveView = () => {
    localStorage.setItem(
      VIEW_KEY,
      JSON.stringify({
        status: statusFilter,
        exception: exceptionFilter,
        vendor: vendorFilter,
        search: searchTerm,
        startDate: dateRange?.[0]?.startDate?.toISOString?.(),
        endDate: dateRange?.[0]?.endDate?.toISOString?.(),
      })
    );
  };

  const resetFilters = () => {
    setStatusFilter("All");
    setExceptionFilter("None");
    setVendorFilter("");
    setSearchTerm("");


    setDateRange([{ startDate: subDays(new Date(), 30), endDate: new Date(), key: "selection" }]);
  };

  const pageSize = 50;
  const filterKey = JSON.stringify([
    unitSlug, unitId, statusFilter, exceptionFilter,
    debouncedVendor, debouncedSearch,
    dateRange?.[0]?.startDate, dateRange?.[0]?.endDate,
  ]);
  const [pageSelection, setPageSelection] = useState({ key: "", page: 1 });
  const page = pageSelection.key === filterKey ? pageSelection.page : 1;
  const [totalRows, setTotalRows] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const changePage = (next) => {
    reportRequestId.current += 1;
    setLoading(true);
    setPageSelection({ key: filterKey, page: next });
  };

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [lastTs, setLastTs] = useState("");
  const [kpi, setKpi] = useState({ spend: 0, pos: 0, receivedPct: 0, exceptions: 0 });

  const [detailPo, setDetailPo] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState("");

  useEffect(() => {
    const onDoc = (e) => {
      if (!openCal) return;
      if (calRef.current && !calRef.current.contains(e.target)) setOpenCal(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [openCal]);

  const fetchTruePoNumber = async (poId) => {
    try {
      if (!poId) return "";
      if (poNumberById[poId]) return poNumberById[poId];

      // Use summary (already tenant-safe in your backend)
      const { params: unitParams } = getUnitParams();
      const res = await api.get(`/purchase-orders/${poId}/summary`, { params: unitParams });
      const n = res?.data?.header?.po_number || "";
      const cleaned = n ? String(n) : "";

      if (cleaned) {
        setPoNumberById((prev) => ({ ...prev, [poId]: cleaned }));
        return cleaned;
      }
      return "";
    } catch {
      return "";
    }
  };

  const warmPoNumbers = async (rows) => {
    const ids = (rows || []).map((r) => r?.po_id).filter((id) => id != null);
    const uniq = [...new Set(ids)].slice(0, 80); // keep light
    await Promise.all(
      uniq.map(async (id) => {
        if (poNumberById[id]) return;
        await fetchTruePoNumber(id);
      })
    );
  };

  const fetchReport = async () => {
    const requestId = ++reportRequestId.current;
    setErr("");
    const { slug, id, params: unitParams } = getUnitParams();

    if (!slug && !id) {
      setErr("Missing unit context. Provide ?unit_id / ?unit_slug (saved in localStorage too).");
      setReportData([]);
      setKpi({ spend: 0, pos: 0, receivedPct: 0, exceptions: 0 });
      return;
    }

    const start = dateRange?.[0]?.startDate;
    const end = dateRange?.[0]?.endDate;

    setLoading(true);
    try {
      const params = {
        ...unitParams,
        start_date: start ? format(start, "yyyy-MM-dd") : undefined,
        end_date: end ? format(end, "yyyy-MM-dd") : undefined,
        status: toBackendStatus(statusFilter),
        vendor: debouncedVendor.trim() || undefined,
        search: debouncedSearch.trim() || undefined,
        exception: exceptionFilter === "Overdue (no receipt)" ? "overdue-none" : undefined,
      };

      const [listRes, kpiRes] = await Promise.all([
        api.get("/reports/procurement", { params: { ...params, page, page_size: pageSize } }),
        api.get("/reports/procurement/kpis", { params }),
      ]);

      const raw = listRes?.data;
      const rows = Array.isArray(raw?.rows) ? raw.rows : Array.isArray(raw) ? raw : [];

      if (requestId !== reportRequestId.current) return;

      const total = Number(raw?.total);
      if (!Number.isInteger(total) || total < 0) throw new Error("Invalid report total");
      const lastPage = Math.max(1, Math.ceil(total / pageSize));
      setTotalRows(total);
      if (page > lastPage) {
        setPageSelection({ key: filterKey, page: lastPage });
        return;
      }
      setReportData(rows);

      // Warm true PO numbers so table shows correct immediately
      await warmPoNumbers(rows);
      if (requestId !== reportRequestId.current) return;

      const kd = kpiRes?.data || {};
      const spend = kd.total_spend ?? kd.total_amount ?? 0;
      const pos = kd.po_count ?? kd.total_pos ?? 0;

      setKpi({
        spend: typeof spend === "string" ? Number(spend) : spend,
        pos: typeof pos === "string" ? Number(pos) : pos,
        receivedPct: kd.received_pct ?? 0,
        exceptions: kd.exceptions ?? 0,
      });

      setLastTs(new Date().toLocaleTimeString());
    } catch (e) {
      if (requestId !== reportRequestId.current) return;
      console.error("Failed to fetch procurement report:", e?.response || e);

      const status = e?.response?.status;
      if (status === 401 || status === 403) setErr("Not authorized. Please log in again and refresh.");
      else setErr(e?.response?.data?.error || "Failed to fetch procurement report");

      setReportData([]);
      setKpi({ spend: 0, pos: 0, receivedPct: 0, exceptions: 0 });
    } finally {
      if (requestId === reportRequestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    return () => { reportRequestId.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    statusFilter,
    exceptionFilter,
    debouncedVendor,
    debouncedSearch,
    dateRange?.[0]?.startDate,
    dateRange?.[0]?.endDate,
    unitSlug,
    unitId,
    page,
  ]);

  const groupedData = useMemo(() => {
    if (!Array.isArray(reportData) || reportData.length === 0) return [];

    // group by po_id primarily; fallback to po_number
    const map = new Map();

    for (const row of reportData) {
      const key = row.po_id ?? row.po_number ?? `row-${map.size}`;
      let g = map.get(key);
      if (!g) {
        g = {
          po_id: row.po_id,
          po_number: row.po_number,
          vendor_name: row.vendor_name,
          planner_name: row.planner_name,
          cost_centre_id: row.cost_centre_id,
          cost_centre_name: row.cost_centre_name,
          status: row.status,
          expected_delivery: row.expected_delivery,
          po_date: row.po_date,
          ordered_total: 0,
          received_total: 0,
          remaining_total: 0,
          net_amount: 0,
          rows: [],
        };
        map.set(key, g);
      }

      const o = Number(row.ordered_quantity || 0);
      const rec = Number(row.received_quantity || 0);

      // remaining: prefer remaining_quantity, fallback to rejected_quantity (legacy)
      const rem = Number(row.remaining_quantity ?? row.rejected_quantity ?? 0);
      const net = Number(row.net_amount || 0);

      if (Number.isFinite(o)) g.ordered_total += o;
      if (Number.isFinite(rec)) g.received_total += rec;
      if (Number.isFinite(rem)) g.remaining_total += rem;
      if (Number.isFinite(net)) g.net_amount += net;

      g.rows.push(row);
    }

    return Array.from(map.values());
  }, [reportData]);

  // The API applies the same search to rows and KPIs.
  const filteredGroupedData = groupedData;

  const hasRows = filteredGroupedData.length > 0;

  const exportToExcel = () => {
    const data = shapeForExport(filteredGroupedData, poNumberById);
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Procurement Report");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "procurement_report.xlsx");
  };

  const exportToCSV = () => {
    const data = shapeForExport(filteredGroupedData, poNumberById);
    const ws = XLSX.utils.json_to_sheet(data);
    const csv = XLSX.utils.sheet_to_csv(ws);
    saveAs(new Blob([csv], { type: "text/csv;charset=utf-8;" }), "procurement_report.csv");
  };

  const openDetail = async (poRow) => {
    if (!poRow?.po_id) return;
    setDetailPo(poRow);
    setDetailErr("");
    setDetailLoading(true);

    try {
      const { params: unitParams } = getUnitParams();
      const res = await api.get(`/purchase-orders/${poRow.po_id}/summary`, { params: unitParams });
      setDetailData(res.data || null);

      const n = res?.data?.header?.po_number;
      if (n) setPoNumberById((prev) => ({ ...prev, [poRow.po_id]: String(n) }));
    } catch (e) {
      console.error("Failed to load PO summary:", e?.response || e);
      setDetailErr(e?.response?.data?.error || "Failed to load PO details");
      setDetailData(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailPo(null);
    setDetailData(null);
    setDetailErr("");
  };

  const applyPreset = (key) => {
    const today = new Date();



    if (key === "today") setDateRange([{ startDate: today, endDate: today, key: "selection" }]);
    else if (key === "7d") setDateRange([{ startDate: subDays(today, 7), endDate: today, key: "selection" }]);
    else if (key === "30d") setDateRange([{ startDate: subDays(today, 30), endDate: today, key: "selection" }]);
    else if (key === "thisMonth")
      setDateRange([{ startDate: startOfMonth(today), endDate: endOfMonth(today), key: "selection" }]);
    else if (key === "lastMonth") {
      const last = subDays(startOfMonth(today), 1);
      setDateRange([{ startDate: startOfMonth(last), endDate: endOfMonth(last), key: "selection" }]);
    } else if (key === "180d") setDateRange([{ startDate: subDays(today, 180), endDate: today, key: "selection" }]);
  };

  const detailHeader = detailData?.header || {};
  const detailItems = Array.isArray(detailData?.items) ? detailData.items : [];

  const clearSearch = () => {
    setSearchTerm("");

  };
  const clearVendor = () => setVendorFilter("");

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(1250px_650px_at_10%_-10%,rgba(59,130,246,0.15),transparent_60%),radial-gradient(1250px_650px_at_110%_10%,rgba(236,72,153,0.12),transparent_60%)]">
      <div className="sticky top-0 z-20 backdrop-blur supports-[backdrop-filter]:bg-zinc-900/60 bg-zinc-900/90 border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate("/procurement")}
              className="border border-white/10 bg-zinc-900/70 hover:bg-zinc-800/90"
            >
              ← Back to Procurement
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                <span className="mr-2">📊</span> Procurement Report
              </h1>
              <p className="text-xs md:text-sm text-zinc-400">
                Choose a period, review delivery exposure, then explore the purchase orders.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={exportToCSV}
              className="shadow-[0_0_0_1px_rgba(255,255,255,.08)]"
              disabled={!hasRows}
              title={!hasRows ? "No data to export" : "Export CSV"}
            >
              Export page CSV
            </Button>
            <Button
              onClick={exportToExcel}
              className="shadow-[0_0_0_1px_rgba(59,130,246,.35)]"
              disabled={!hasRows}
              title={!hasRows ? "No data to export" : "Export Excel"}
            >
              Export page Excel
            </Button>
          </div>
        </div>
        <nav aria-label="Report sections" className="mx-auto max-w-7xl px-4 sm:px-6 pb-3 flex flex-wrap gap-2 text-sm text-white">
          {[["report-filters", "Filters"], ["report-overview", "Overview"], ["report-decisions", "Ownership & exposure"], ["report-orders", "Purchase orders"]].map(([id, label]) => (
            <a key={id} href={`#${id}`} className="rounded-full border border-white/15 px-3 py-2 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300">{label}</a>
          ))}
        </nav>
      </div>

      <div className="mx-auto max-w-7xl px-6 py-6 text-white">
        <div id="report-filters" className="scroll-mt-40 rounded-2xl border border-white/10 bg-white/[0.04] p-4 md:p-5 mb-6">
          <h2 className="text-lg font-semibold mb-1">Filter purchase orders</h2>
          <p className="text-sm text-zinc-400 mb-4">Date range uses PO creation date. Filters update automatically.</p>
          <details className="mb-4 rounded-lg border border-white/10 p-3">
            <summary className="cursor-pointer text-sm text-zinc-300">Unit settings · {unitSlug || unitId || "Select a unit"}</summary>
          <div className="mt-3 mb-3 grid grid-cols-1 md:grid-cols-6 gap-2 items-center">
            <div className="md:col-span-2">
              <Input
                placeholder="Unit slug (e.g. chelsea-fc)"
                value={unitSlugInput}
                onChange={(e) => setUnitSlugInput(e.target.value)}
                className="bg-zinc-900/60 border-zinc-700/60"
              />
            </div>
            <div className="md:col-span-2">
              <Input
                placeholder="Unit ID (e.g. 3)"
                value={unitIdInput}
                onChange={(e) => setUnitIdInput(e.target.value)}
                className="bg-zinc-900/60 border-zinc-700/60"
              />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button
                onClick={() => {
                  saveUnitContext();
                  setTimeout(() => {


                    fetchReport();
                  }, 0);
                }}
              >
                Set Unit &amp; Refresh
              </Button>
              <Button variant="secondary" onClick={resetFilters}>
                Reset
              </Button>
            </div>
          </div>

          <div className="text-xs text-zinc-400 mb-2">
            Detected unit: {unitSlug ? <code>{unitSlug}</code> : <em>none</em>} /{" "}
            {unitId ? <code>{unitId}</code> : <em>none</em>}
          </div>

          </details>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
            <div className="lg:col-span-4">
              <div className="flex gap-2">
                <Input
                  aria-label="Search purchase orders" placeholder="Search PO number or item…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-zinc-900/60 border-zinc-700/60 focus-visible:ring-1 focus-visible:ring-sky-500"
                />
                <Button
                  variant="secondary"
                  onClick={clearSearch}
                  className="border border-white/10 bg-zinc-900/60 hover:bg-zinc-800/80"
                  title="Clear search"
                >
                  Clear
                </Button>
              </div>
              <div className="mt-1 text-[11px] text-zinc-500">
                Search matches DB PO number when available (CHEL26367 / OVOW26007).
              </div>
            </div>

            <div className="lg:col-span-2">
              <Select
                value={statusFilter}
                onValueChange={(v) => {


                  setStatusFilter(v);
                }}
              >
                <SelectTrigger aria-label="Exception filter" className="bg-zinc-900/60 border-zinc-700/60 focus:ring-sky-500">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  {UI_STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="lg:col-span-3">
              <Select
                value={exceptionFilter}
                onValueChange={(v) => {


                  setExceptionFilter(v);
                }}
              >
                <SelectTrigger className="bg-zinc-900/60 border-zinc-700/60 focus:ring-sky-500">
                  <SelectValue placeholder="Exceptions" />
                </SelectTrigger>
                <SelectContent>
                  {EXCEPTION_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="lg:col-span-3">
              <div className="flex gap-2">
                <Input
                  aria-label="Filter by vendor" placeholder="Vendor…"
                  value={vendorFilter}
                  onChange={(e) => {


                    setVendorFilter(e.target.value);
                  }}
                  className="bg-zinc-900/60 border-zinc-700/60 focus-visible:ring-1 focus-visible:ring-sky-500"
                />
                <Button
                  variant="secondary"
                  onClick={clearVendor}
                  className="border border-white/10 bg-zinc-900/60 hover:bg-zinc-800/80"
                  title="Clear vendor"
                >
                  Clear
                </Button>
              </div>
            </div>

            <div className="lg:col-span-12 flex items-center justify-between gap-2 mt-1">
              <div className="flex flex-wrap gap-2">
                {[
                  { k: "today", label: "Today" },
                  { k: "7d", label: "7d" },
                  { k: "30d", label: "30d" },
                  { k: "thisMonth", label: "This Month" },
                  { k: "lastMonth", label: "Last Month" },
                  { k: "180d", label: "180d" },
                ].map((p) => (
                  <button
                    key={p.k}
                    onClick={() => applyPreset(p.k)}
                    className="px-2.5 py-1 rounded-full text-xs bg-zinc-900/60 hover:bg-zinc-800/80 border border-white/10"
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              <div className="relative ml-auto" ref={calRef}>
                <Button
                  variant="secondary"
                  onClick={() => setOpenCal((v) => !v)}
                  className="bg-zinc-900/60 border border-white/10"
                >
                  Date: {format(dateRange[0].startDate, "MMM d, yyyy")} → {format(dateRange[0].endDate, "MMM d, yyyy")}
                </Button>

                {openCal && (
                  <div className="absolute right-0 mt-2 z-30 rounded-xl overflow-hidden border border-white/10 bg-zinc-900 shadow-2xl">
                    <DateRange
                      editableDateInputs
                      moveRangeOnFirstSelection={false}
                      ranges={dateRange}
                      onChange={(item) => {
                        const sel = item?.selection;
                        const next = clampDateRangeToToday({
                          startDate: sel?.startDate ?? dateRange[0].startDate,
                          endDate: sel?.endDate ?? dateRange[0].endDate,
                        });


                        setDateRange([next]);
                      }}
                    />
                    <div className="flex justify-end gap-2 p-2 border-t border-white/10">
                      <Button variant="secondary" onClick={() => setOpenCal(false)}>
                        Close
                      </Button>
                      <Button
                        onClick={() => {
                          setOpenCal(false);


                          fetchReport();
                        }}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-12 mt-3 flex items-center gap-3">
              <Button
                variant="secondary"
                onClick={() => {


                  fetchReport();
                }}
                className="border border-white/10 bg-zinc-900/60 hover:bg-zinc-800/80"
              >
                Refresh
              </Button>
              <Button onClick={saveView} className="bg-sky-600/80 hover:bg-sky-600">
                Save View
              </Button>
              <Button variant="secondary" onClick={resetFilters}>
                Reset
              </Button>

              <span className="ml-auto text-xs text-zinc-400">
                {hasRows ? `${((page - 1) * pageSize + 1).toLocaleString()}–${((page - 1) * pageSize + filteredGroupedData.length).toLocaleString()} of ${totalRows.toLocaleString()} POs` : "0 rows"}
                {lastTs ? ` • updated ${lastTs}` : ""}
              </span>

              {err && <span className="text-rose-400 text-sm">❌ {err}</span>}
            </div>

            {unitMissing && (
              <div className="lg:col-span-12 mt-1 text-xs text-amber-300">
                Tip: enter unit slug or ID above, or add <code>?unit_slug=your-slug</code> / <code>?unit_id=123</code> to the URL.
              </div>
            )}
          </div>
        </div>

        <div id="report-overview" className="scroll-mt-40 grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Recorded value (unreconciled)", value: currency(kpi.spend) },
            { label: "POs", value: kpi.pos?.toLocaleString?.() ?? 0 },
            { label: "Quantity received %", value: `${Math.round(kpi.receivedPct || 0)}%` },
            { label: "Past due · no receipts", value: kpi.exceptions ?? 0 },
          ].map((c, i) => (
            <div
              key={i}
              className="rounded-2xl p-4 bg-white/[0.04] border border-white/10 shadow-[inset_0_1px_0_0_rgba(255,255,255,.05)]"
            >
              <div className="text-xs text-zinc-400">{c.label}</div>
              <div className={`mt-1 text-xl font-semibold ${loading ? "animate-pulse" : ""}`}>
                {loading ? <span className="inline-block h-5 w-20 rounded bg-white/10" /> : err ? "Unavailable" : c.value}
              </div>
            </div>
          ))}
        </div>


        <p className="mb-6 text-sm text-amber-200">Recorded value mixes order and invoice amounts. Financial reconciliation is pending; this is not a cash forecast.</p>
        <div id="report-decisions" className="scroll-mt-40" />
        {!unitMissing && (
          <ProcurementDecisionBreakdowns
            params={{
              ...getUnitParams().params,
              start_date: dateRange?.[0]?.startDate
                ? format(dateRange[0].startDate, "yyyy-MM-dd")
                : undefined,
              end_date: dateRange?.[0]?.endDate
                ? format(dateRange[0].endDate, "yyyy-MM-dd")
                : undefined,
              status: toBackendStatus(statusFilter),
              vendor: debouncedVendor.trim() || undefined,
              search: debouncedSearch.trim() || undefined,
              exception:
                exceptionFilter === "Overdue (no receipt)"
                  ? "overdue-none"
                  : undefined,
            }}
          />
        )}
        <h2 id="report-orders" className="scroll-mt-40 mt-6 mb-3 text-lg font-semibold">Purchase orders</h2>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/60 backdrop-blur">
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-zinc-900/80 backdrop-blur border-b border-white/10">
                <tr className="text-zinc-200">
                  <th className="px-3 py-2 text-left font-medium">PO Number</th>
                  <th className="px-3 py-2 text-left font-medium">Vendor</th>
                  <th className="px-3 py-2 text-left font-medium">Planner</th>
                  <th className="px-3 py-2 text-left font-medium">Cost Centre (Name + ID)</th>
                  <th className="px-3 py-2 text-right font-medium">Ordered</th>
                  <th className="px-3 py-2 text-right font-medium">Received</th>
                  <th className="px-3 py-2 text-right font-medium">Remaining</th>
                  <th className="px-3 py-2 text-right font-medium">Net (GBP)</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Expected</th>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-white/5">
                {loading && (
                  <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </>
                )}

                {!loading &&
                  hasRows &&
                  filteredGroupedData.map((row, idx) => {
                    const shownPoNo = displayPoNo(row.po_id, row.po_number, poNumberById);
                    return (
                      <tr
                        key={`${row.po_id ?? shownPoNo}-${idx}`}
                        className="hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => openDetail(row)}
                        title="Click to view PO contents"
                      >
                        <td className="px-3 py-2 font-medium text-zinc-100">{shownPoNo}</td>
                        <td className="px-3 py-2">{row.vendor_name}</td>
                        <td className="px-3 py-2">{row.planner_name || "—"}</td>
                        <td className="px-3 py-2">
                          {row.cost_centre_name || "—"}
                          {row.cost_centre_id ? ` (${row.cost_centre_id})` : ""}
                        </td>

                        <td className="px-3 py-2 text-right tabular-nums">{row.ordered_total}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.received_total}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{row.remaining_total}</td>

                        <td className="px-3 py-2 text-right tabular-nums">{currency(row.net_amount)}</td>
                        <td className="px-3 py-2">
                          <StatusPill value={row.status} />
                        </td>
                        <td className="px-3 py-2">
                          {row.expected_delivery ? new Date(row.expected_delivery).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-3 py-2">
                          {row.po_date ? new Date(row.po_date).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    );
                  })}

                {!loading && !hasRows && (
                  <tr>
                    <td colSpan={11} className="px-3 py-10 text-center text-zinc-400">
                      <div className="mx-auto mb-2 h-10 w-10 rounded-full bg-white/5 grid place-items-center">
                        <span className="opacity-70">🗂️</span>
                      </div>
                      No data for this filter range.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <nav aria-label="Purchase order pages" className="mx-auto max-w-7xl px-6 pb-6 flex flex-wrap items-center justify-between gap-3 text-white">
        <span aria-live="polite" className="text-sm text-zinc-300">
          {loading ? "Loading purchase orders…" : err ? "Report unavailable" :
            totalRows === 0 ? "No matching purchase orders" :
            `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalRows)} of ${totalRows} POs`}
        </span>
        <div className="flex items-center gap-3">
          <Button variant="secondary" disabled={loading || !!err || page <= 1}
            onClick={() => changePage(page - 1)} aria-label="Previous purchase order page">
            ← Previous
          </Button>
          <span className="text-sm">Page {page} of {totalPages}</span>
          <Button variant="secondary" disabled={loading || !!err || page >= totalPages}
            onClick={() => changePage(page + 1)} aria-label="Next purchase order page">
            Next →
          </Button>
        </div>
      </nav>

      {/* ✅ FIXED MODAL (Portal) — no feature loss, only layout fix */}
      <Transition appear show={!!detailPo} as={Fragment}>
        <Dialog as="div" className="relative z-[9999]" onClose={closeDetail}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-zinc-950 shadow-2xl">
                  <div className="flex items-start justify-between px-4 py-3 border-b border-white/10">
                    <div>
                      <div className="text-xs text-zinc-400 mb-1">PO Details</div>
                      <Dialog.Title className="text-lg font-semibold text-white">
                        {detailHeader.po_number ||
                          (detailPo ? displayPoNo(detailPo.po_id, detailPo.po_number, poNumberById) : "—")}{" "}
                        <span className="text-xs font-normal text-zinc-400">
                          {detailHeader.status || detailPo?.status || ""}
                        </span>
                      </Dialog.Title>
                      <div className="text-xs text-zinc-400 mt-1">
                        {detailHeader.vendor_name || detailPo?.vendor_name || "—"}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {detailLoading && <span className="text-xs text-zinc-400">Loading…</span>}
                      {detailErr && <span className="text-xs text-rose-400">{detailErr}</span>}
                      <Button variant="ghost" size="icon" onClick={closeDetail} className="text-zinc-300">
                        ✕
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 overflow-auto">
                    <table className="min-w-full text-xs">
                      <thead className="border-b border-white/10 text-zinc-300">
                        <tr>
                          <th className="px-2 py-1 text-left font-medium">Item Code</th>
                          <th className="px-2 py-1 text-left font-medium">Item Name</th>
                          <th className="px-2 py-1 text-right font-medium">Ordered</th>
                          <th className="px-2 py-1 text-right font-medium">Received</th>
                          <th className="px-2 py-1 text-right font-medium">Ignored</th>
                          <th className="px-2 py-1 text-right font-medium">Backordered</th>
                          <th className="px-2 py-1 text-right font-medium">Redelivery</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {detailItems.length === 0 && !detailLoading && (
                          <tr>
                            <td colSpan={7} className="px-2 py-6 text-center text-zinc-400">
                              No item breakdown available for this PO.
                            </td>
                          </tr>
                        )}

                        {detailItems.map((it) => (
                          <tr key={it.po_item_id}>
                            <td className="px-2 py-1">{it.item_code}</td>
                            <td className="px-2 py-1">{it.item_name}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{it.quantity}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{it.received_so_far}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{it.ignored_so_far}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{it.backordered_so_far}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{it.redelivery_requested_qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="mt-3 text-[11px] text-zinc-500">
                      Note: “Remaining” in the report is outstanding quantity to receive.
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}